/**
 * TagsField - Tag input with add/remove functionality
 *
 * Allows users to add and remove tags with keyboard support.
 */

import { useState, useCallback, useRef } from 'react';
import { X } from 'lucide-react';
import { FormField } from '../FormField';
import { formStyles, mergeStyles } from '../formStyles';

export interface TagsFieldProps {
  /** Field label */
  label: string;
  /** Current tags */
  tags: string[];
  /** Handler for adding a tag */
  onAdd: (tag: string) => void;
  /** Handler for removing a tag */
  onRemove: (tag: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Suggested tags (for autocomplete) */
  suggestions?: string[];
  /** Error message */
  error?: string;
  /** Help text */
  hint?: string;
  /** Maximum number of tags */
  maxTags?: number;
  /** Custom className */
  className?: string;
}

export function TagsField({
  label,
  tags,
  onAdd,
  onRemove,
  placeholder = 'Add tag...',
  suggestions = [],
  error,
  hint,
  maxTags,
  className,
}: TagsFieldProps) {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleAdd = useCallback(() => {
    const trimmed = inputValue.trim();
    if (trimmed && !tags.includes(trimmed)) {
      if (!maxTags || tags.length < maxTags) {
        onAdd(trimmed);
        setInputValue('');
      }
    }
  }, [inputValue, tags, onAdd, maxTags]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAdd();
      } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
        onRemove(tags[tags.length - 1]);
      }
    },
    [handleAdd, inputValue, tags, onRemove]
  );

  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      if (!tags.includes(suggestion)) {
        if (!maxTags || tags.length < maxTags) {
          onAdd(suggestion);
        }
      }
      setShowSuggestions(false);
      inputRef.current?.focus();
    },
    [tags, onAdd, maxTags]
  );

  const filteredSuggestions = suggestions.filter(
    (s) =>
      !tags.includes(s) &&
      s.toLowerCase().includes(inputValue.toLowerCase())
  );

  const containerStyle = mergeStyles(
    formStyles.tagsContainer,
    isFocused && formStyles.inputFocus,
    error && formStyles.inputError
  );

  const canAddMore = !maxTags || tags.length < maxTags;

  return (
    <FormField label={label} error={error} hint={hint} className={className}>
      <div style={{ position: 'relative' }}>
        <div
          style={containerStyle}
          onClick={() => inputRef.current?.focus()}
        >
          {tags.map((tag) => (
            <span key={tag} style={formStyles.tag}>
              {tag}
              <button
                type="button"
                style={formStyles.tagRemove}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(tag);
                }}
                aria-label={`Remove ${tag}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
          {canAddMore && (
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                setIsFocused(true);
                setShowSuggestions(true);
              }}
              onBlur={() => {
                setIsFocused(false);
                // Delay hiding suggestions to allow clicks
                setTimeout(() => setShowSuggestions(false), 150);
                handleAdd();
              }}
              placeholder={tags.length === 0 ? placeholder : ''}
              style={formStyles.tagInput}
            />
          )}
        </div>

        {/* Suggestions dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && inputValue && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: 'white',
              border: '1px solid var(--line, #e5e7eb)',
              borderRadius: '6px',
              marginTop: '4px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              zIndex: 10,
              maxHeight: '150px',
              overflow: 'auto',
            }}
          >
            {filteredSuggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => handleSuggestionClick(suggestion)}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--bg-muted, #f3f4f6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'none';
                }}
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </div>
    </FormField>
  );
}
